<template>
  <component
    :is="to ? nuxtLink : 'div'"
    data-test-id="product-card"
    :no-prefetch="true"
    :class="$style['product-card']"
    :to="to">
    <div :class="$style['product-card__image']">
      <ImageSwiper
        :images="images"
        :videos="product.videos"
        :loading="loading"
        :alt="`${product.title} ${
          product.colorName ? product.colorName.toLowerCase() : ''
        }`" />
      <div
        v-if="!compact && !disableTag"
        :class="$style['product-card__image__overlay']">
        <LazyTag
          v-if="isDayDeal && !compact"
          shadow
          :class="$style['tag']"
          type="day-deal" />
        <LazyTag
          v-for="label in filteredLabels"
          :key="label.type"
          shadow
          :class="$style['tag']"
          :type="label.type">
          {{ label.title }}
        </LazyTag>
      </div>

      <div :class="$style['product-card__image__top']" />
    </div>

    <div :class="$style['product-card__text']">
      <div
        :class="$style['product-card__title']"
        v-html="product.title" />
    </div>

    <div
      :class="[
        $style['product-card__subtitle'],
        {[$style[`product-card__subtitle--small`]] : cardSize === 'small'}]">
      <ProductPrice
        v-if="product.price && !hidePrice"
        :price="product.price"
        :sale="product.originalPrice"
        :percent="product.salePercent" />

      <div
        v-if="!compact && showSizes && !disableSizes"
        :class="$style['product-card__subtitle__right']">
        <LazyProductSizeStack :sizes="product.sizes || product.avaliableSizes" />
        <span v-if="isBacksoon || isComingsoon || isUnavailable">{{
          $t('product/subscribe-now-to-product')
        }}</span>
      </div>
    </div>
    <div
      v-if="!disableColors"
      :class="$style['product-card__colors']">
      <LazyProductColorStack :colors="product.alternativeColors" />
    </div>
    <div>
      <small
        v-if="showProductId"
        class="h-color-grey h-font-size-s">
        Product nr. {{ product.pId }}
      </small>
    </div>
    <div
      v-if="!!$slots['card-button']"
      :class="$style['product-card__button']">
      <slot name="card-button" />
    </div>
  </component>
</template>

<script setup>
const nuxtLink = resolveComponent('nuxt-link')
const config = useRuntimeConfig()

const props = defineProps({
  showProductId: {
    type: Boolean,
    default: false
  },
  disableColors: {
    type: Boolean,
    default: false
  },
  disableSizes: {
    type: Boolean,
    default: false
  },
  disabledSwipe: {
    type: Boolean,
    default: false
  },
  disableTag: {
    type: Boolean,
    default: false
  },
  aspectRatio: {
    type: String,
    default: 'portrait'
  },
  loading: {
    type: String,
    default: 'lazy'
  },
  compact: {
    type: Boolean,
    default: false
  },
  product: {
    type: [Array, Object],
    default: null
  },
  to: {
    type: Object,
    default: null
  },
  hidePrice: {
    type: Boolean,
    default: false
  },
  cardSize: {
    type: String,
    default: 'medium'
  }
})

const images = computed(() => {
  if (!props.product.image && !props.product.images) { return false }
  const images =
        typeof props.product.image !== 'undefined'
          ? [props.product.image]
          : props.product.images
  return images
    .map(image => {
      return {
        full: `${config.public.cdnURL}pictures/full/${image}.webp`,
        mobile: `${config.public.cdnURL}pictures/${props.cardSize === 'large' ? 'full' : 'medium'}/${image}.webp`
      }
    })
    .slice(0, 2)
})
const showSizes = computed(() => {
  if (!props.product.sizes && !props.product.avaliableSizes) { return false }
  if (props.product.category.includes('228')) { return false } // Exclude phones for now. TODO: Fix this elsewhere
  return true
})
const filteredLabels = computed(() => {
  if (!props.product || !props.product.labels) { return false }
  return props.product.labels.filter(label => {
    return label.type !== 'day-deal'
  })
})
const isDayDeal = computed(() => {
  if (!props.product.labels) { return false }
  return props.product.labels.find(label => {
    return label.type === 'day-deal'
  })
})
const isBacksoon = computed(() => {
  if (!props.product.labels) { return false }
  return props.product.labels.find(label => {
    return label.type === 'backsoon'
  })
})
const isComingsoon = computed(() => {
  if (!props.product.labels) { return false }
  return props.product.labels.find(label => {
    return label.type === 'comingsoon'
  })
})
const isUnavailable = computed(() => {
  if (!props.product.labels) { return false }
  return props.product.labels.find(label => {
    return label.type === 'unavailable'
  })
})

</script>

<style lang="scss" module>
.product-card {
  border-radius: var(--border-radius);
  color: var(--black);
  display: flex;
  flex-direction: column;
  position: relative;
  height: auto;
  width: 100%;
  overflow: hidden;
  text-decoration: none;
  -webkit-touch-callout: none;

  &:focus-visible {
    box-shadow: var(--focus-visible);
  }

  &__text {
    padding-top: calc(var(--spacing) * 0.5);
    width: 100%;
  }

  &__title {
    font-size: var(--font-size-s);
    line-height: 1.25;
    font-weight: var(--font-weight-bold);
    margin-top: calc(var(--spacing) * 0.5);
    margin-bottom: var(--spacing);
    text-wrap: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    @media (min-width: #{$breakpoint-small-desktop}) {
      margin-bottom: calc(var(--spacing) * 0.6);
      font-size: var(--font-size);
    }
  }

  &__subtitle {
    display: inline-flex;
    justify-content: space-between;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;

    &__right {
      align-items: center;
      justify-content: flex-end;
      display: flex;
      flex-wrap: wrap;
      overflow: hidden;
      width: 100%;
      color: var(--grey-dark);
      font-size: var(--font-size-xs);
      line-height: var(--line-height);
      padding-left: var(--spacing);

      @media (max-width: #{$breakpoint-mobile}) {
        justify-content: flex-start;
        padding-left: 0;
      }
    }

    @media (max-width: #{$breakpoint-mobile}) {
      align-items: flex-start;
      justify-content: flex-start;
      display: flex;
      flex-direction: column;
    }

    &--small {
      @media (max-width: #{$breakpoint-small-desktop}) {
        align-items: flex-start;
        justify-content: flex-start;
        display: flex;
        flex-direction: column;

        > * {
          padding-left: 0;
          justify-content: flex-start;
        }
      }
    }
  }

  &__colors {
    display: flex;
    justify-content: flex-end;
    flex-direction: row-reverse;
    min-height: calc(var(--spacing) * 3);
    width: 100%;
  }

  &__image {
    aspect-ratio: 10/16;
    border-radius: var(--border-radius);
    overflow: hidden;
    display: inline-flex;
    position: relative;

    @include backgroundPulse;

    &__overlay {
      display: flex;
      align-items: flex-start;
      flex-flow: column wrap;
      gap: var(--spacing);
      position: absolute;
      bottom: var(--spacing);
      left: var(--spacing);
      right: var(--spacing);
      width: auto;
      z-index: 3;
    }

    &__bottom {
      align-items: center;
      display: flex;
      padding: calc(var(--spacing) * 2);
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
    }

    &__top {
      align-items: flex-start;
      display: flex;
      flex-flow: column wrap;
      padding: var(--spacing);
      gap: var(--spacing);
      line-height: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
    }
  }

  &__button {
    display: flex;
    justify-content: flex-end;
    position: absolute;
    gap: calc(var(--spacing) * 0.5);
    top: 0;
    left: 0;
    right: 0;
    padding: var(--spacing);
    width: auto;
    z-index: 2;

    @media (max-width: #{$breakpoint-mobile}) {
      padding: var(--spacing);
      gap: 0;
    }

    & > * {
      margin-left: var(--spacing);
    }
  }

  &[href]:active,
  &[href]:hover {
    color: var(--grey);
  }

  .tag {
    z-index: 1;
  }
}

</style>
